import { format, isEqual, isWeekend } from "date-fns";
import { WALLEX_KYC_STATUS } from "src/ipm-shared/store/model/OnBoarding/consts";
import { CustomerFee } from "src/ipm-shared/store/model/PaymentCollectionRequest/reducers";

// A bin rule can be shorter or longer than the bin we know from the card,
// so both sides are compared on their common length.
const isBinMatched = (cardBin: string, bins?: string[] | null) => {
  if (!cardBin || !bins || bins.length === 0) {
    return false;
  }

  return bins.some(bin => {
    if (!bin) {
      return false;
    }
    const length = Math.min(cardBin.length, bin.length);
    return cardBin.slice(0, length) === bin.slice(0, length);
  });
};

const PaymentUtil = {
  convertFrequencyString: (frequency: string) => {
    let result = "";

    switch (frequency) {
      case "7 days":
        result = "Weekly";
        break;
      case "1 mon":
        result = "Monthly";
        break;

      case "14 days":
        result = "Biweekly";
        break;

      case "3 mons":
        result = "Quarterly";
        break;

      case "6 mons":
        result = "Biannually";
        break;

      default:
        result = "Once";
    }
    return result;
  },

  payoutTimeBetween: (
    dateLeft: string,
    dateRight: string,
    exclusionDates: Date[]
  ) => {
    const from = new Date(format(new Date(dateLeft), "YYYY-MM-DD"));
    const to = new Date(format(new Date(dateRight), "YYYY-MM-DD"));
    let finalRangeDays: any[];

    const rangeDays = [];
    const d1 = from;
    while (d1 < to) {
      d1.setDate(d1.getDate() + 1);
      rangeDays.push(new Date(d1));
    }

    finalRangeDays = rangeDays
      .filter(
        d =>
          exclusionDates.filter((ex: any) => isEqual(new Date(ex), new Date(d)))
            .length === 0
      )
      .filter(d => !isWeekend(d));

    return finalRangeDays;
  },

  convertToSGD: (amount: number, rate: number): number => {
    return Math.floor(amount * rate);
  },

  isUSDPaymentInHK: (
    accountCountryId: number,
    currencyId: number,
    countryId?: number
  ) => {
    return (
      accountCountryId === 3 &&
      currencyId === 73 &&
      countryId !== undefined &&
      countryId === 3
    );
  },

  isWallexKycInProgress: (wallexKycStatus: number) => {
    return (
      wallexKycStatus !== WALLEX_KYC_STATUS.APPROVED &&
      wallexKycStatus !== WALLEX_KYC_STATUS.REJECTED &&
      wallexKycStatus !== WALLEX_KYC_STATUS.INACTIVE
    );
  },

  convertIpmMccId: (ipmMccId: string) => {
    return ipmMccId ? Number(ipmMccId.split("_")[1]) : 0;
  },

  formatIpmMerchantValue: (data: string) => {
    const str = data.split("_");
    return `${str[0]}_${str[1]}`;
  },
  /**
   * Picks the rate of a card brand, honouring the bin restrictions of each rate:
   * - a rate limited to `bins` only applies when the card bin is in that list;
   * - a rate is skipped when the card bin is in its `excludeBins`.
   * Returns undefined when no rate applies to this brand/bin.
   */
  findCustomerFee: (
    customerFee: CustomerFee[] = [],
    cardBrandId: number,
    cardBin: string = ""
  ): CustomerFee | undefined => {
    const brandFees = customerFee.filter(
      item => item && item.brandId === cardBrandId
    );

    // Bin specific rates win over the general rate of the brand.
    const binFee = brandFees.find(item => isBinMatched(cardBin, item.bins));
    if (binFee) {
      return binFee;
    }

    return brandFees.find(
      item =>
        !(item.bins && item.bins.length > 0) &&
        !isBinMatched(cardBin, item.excludeBins)
    );
  },

  getInsurancePayeeName: (name: string) => {
    if (
      [
        "Health and Life policy (UOB)",
        "Health/MINDEF/MHA policy (DBS)",
        "Life policy (DBS)",
        "Other Life, Health, Savings & Retirement Plans (DBS)",
        "Zurich Life Singapore policy (DBS)"
      ].includes(name)
    ) {
      return "Singapore Life Ltd";
    }
    if (["501036925001 (OCBC)", "501219000001 (OCBC)"].includes(name)) {
      return "The Great Eastern Life Assurance Co Ltd";
    }
    return name;
  }
};

export default PaymentUtil;
